const CONTAINER_SELECTOR = '[ref="acceleratedCheckoutButtonContainer"]';
const BLOCKED_CLASS = 'accelerated-checkout-block--upload-blocked';

/**
 * Shopify only stores file line item properties when the browser posts the product
 * form as multipart/form-data to /cart/add. Accelerated checkout builds its own
 * checkout from the variant and the plain form values, so a file input contributes
 * nothing but the browser's placeholder path and the order arrives without artwork.
 *
 * Only fields the customer can actually submit count. Upload blocks that are hidden
 * by conditional display, a design selection, or a product tag rule disable their
 * inputs (or never render at all), so those products keep accelerated checkout.
 *
 * @param {HTMLInputElement} input
 * @returns {boolean}
 */
const isInUse = (input) => !input.disabled && input.closest('[hidden]') == null;

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
const collectsFiles = (form) =>
  Array.from(form.elements).some(
    (element) => element instanceof HTMLInputElement && element.type === 'file' && isInUse(element)
  );

/** @param {Element} container */
const syncContainer = (container) => {
  const form = container.closest('form');
  if (!(form instanceof HTMLFormElement)) return;

  container.classList.toggle(BLOCKED_CLASS, collectsFiles(form));
};

/** @param {ParentNode} [root] */
const syncAll = (root) => {
  for (const container of (root ?? document).querySelectorAll(CONTAINER_SELECTOR)) {
    syncContainer(container);
  }
};

let pendingSync = false;

/**
 * Conditional option blocks enable and disable their inputs while handling the
 * change event, so wait a frame before reading the form back.
 */
const scheduleSync = () => {
  if (pendingSync) return;

  pendingSync = true;
  requestAnimationFrame(() => {
    pendingSync = false;
    syncAll();
  });
};

syncAll();

document.addEventListener('change', scheduleSync, { capture: true });
document.addEventListener('input', scheduleSync, { capture: true });
document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  syncAll(target instanceof Element ? target : undefined);
});

// Upload blocks are also shown and hidden outside of form events, for instance when a
// pre-made design is picked or when another script moves blocks into the form.
new MutationObserver(scheduleSync).observe(document.documentElement, {
  subtree: true,
  childList: true,
  attributes: true,
  attributeFilter: ['disabled', 'hidden'],
});
